import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@apuestapr/ui';
import { useEffect, type ReactElement } from 'react';
import { useBlocker } from 'react-router-dom';

export interface UnsavedChangesGuardProps {
  readonly dirty: boolean;
}

/**
 * Protects in-app PUSH/REPLACE/POP navigation with the shared Dialog and hard unloads with the
 * browser-owned prompt. The caller decides dirtiness after normalizing values against its baseline.
 */
export const UnsavedChangesGuard = ({ dirty }: UnsavedChangesGuardProps): ReactElement => {
  const blocker = useBlocker(dirty);

  useEffect(() => {
    if (!dirty) return undefined;
    const preventUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [dirty]);

  return (
    <Dialog
      open={blocker.state === 'blocked'}
      onOpenChange={(open) => {
        if (!open && blocker.state === 'blocked') blocker.reset();
      }}
    >
      <DialogContent closeLabel="Permanecer en el formulario">
        <DialogHeader>
          <DialogTitle>Cambios sin guardar</DialogTitle>
          <DialogDescription>
            Si descartas los cambios, perderás lo que editaste desde el último guardado.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              if (blocker.state === 'blocked') blocker.reset();
            }}
          >
            Permanecer
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (blocker.state === 'blocked') blocker.proceed();
            }}
          >
            Descartar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
